import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ReactPlayer from 'react-player';
import {
    ChevronLeft, BookOpen, MonitorPlay, FileText,
    ExternalLink, CheckCircle, Lock, Menu, X,
    ChevronDown, ChevronRight, ArrowLeft
} from 'lucide-react';
import { useKuizyTheme } from '@/styles/kuizyTheme';
import { getCourseContents, getAppLessons } from '@/api/kuizy';
import { API_BASE } from '@/settings/master';

const LearnerContentPage = () => {
    const { courseId, attachmentId } = useParams();
    const navigate = useNavigate();
    const { t, theme } = useKuizyTheme();

    const [contents, setContents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentAttachment, setCurrentAttachment] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(true);

    // Store lessons for each content to avoid re-fetching
    const [contentLessons, setContentLessons] = useState({});
    // Store open state for content accordions
    const [openContents, setOpenContents] = useState({});

    // Fetch Course Structure
    useEffect(() => {
        const fetchCourseData = async () => {
            try {
                setLoading(true);
                const res = await getCourseContents(courseId);
                const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
                setContents(data);

                // Pre-load lessons for all contents to find the current attachment
                // In a real app with huge data, this might be optimized, but for now we need to find where the attachment is.
                // Alternatively, we could fetch lessons on demand, but we need to know the structure to highlight the active item.
                const lessonsMap = {};

                // Helper to fetch lessons for a content
                const fetchLessonsForContent = async (content) => {
                    const cid = content.content_id || content.id;
                    try {
                        const lRes = await getAppLessons({ contentId: cid });
                        return {
                            cid,
                            lessons: Array.isArray(lRes.data) ? lRes.data : (lRes.data?.data || [])
                        };
                    } catch (e) {
                        console.error(e);
                        return { cid, lessons: [] };
                    }
                };

                const results = await Promise.all(data.map(fetchLessonsForContent));
                results.forEach(({ cid, lessons }) => {
                    lessonsMap[cid] = lessons;
                });

                setContentLessons(lessonsMap);

                // Find current attachment and expand sections
                let found = false;
                for (const content of data) {
                    const cid = content.content_id || content.id;
                    const lessons = lessonsMap[cid] || [];

                    for (const lesson of lessons) {
                        const atts = lesson.attachments || [];
                        const match = atts.find(a => (a.id || a.attachment_id).toString() === attachmentId);

                        if (match) {
                            setCurrentAttachment({ ...match, lessonName: lesson.lesson_name || lesson.name, contentName: content.content_name || content.name });
                            setOpenContents(prev => ({ ...prev, [cid]: true }));
                            found = true;
                            break;
                        }
                    }
                    if (found) break;
                }

            } catch (error) {
                console.error("Failed to fetch course contents", error);
            } finally {
                setLoading(false);
            }
        };

        fetchCourseData();
    }, [courseId, attachmentId]);

    const handleAttachmentClick = (att) => {
        const urlStr = (att.download_url || att.url || '').toLowerCase();
        const nameStr = (att.name || att.file_name || '').toLowerCase();
        const isPdf = att.type === 'pdf' || nameStr.endsWith('.pdf') || urlStr.endsWith('.pdf');

        if (att.type === 'video' || att.type === 'youtube' || att.type === 'link' || isPdf) {
            navigate(`/learn/course/${courseId}/play/${att.id || att.attachment_id}`);
        } else {
            // For other types, open in new tab / download
            const isDoc = att.type === 'doc' || att.type === 'docx' || nameStr.endsWith('.doc') || nameStr.endsWith('.docx') || urlStr.endsWith('.doc') || urlStr.endsWith('.docx');
            if (isDoc) {
                const link = document.createElement('a');
                link.href = att.download_url || att.url;
                link.setAttribute('download', att.name || att.file_name || 'download');
                link.setAttribute('target', '_blank');
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                window.open(att.download_url || att.url, '_blank');
            }
        }
    };

    const toggleContent = (contentId) => {
        setOpenContents(prev => ({
            ...prev,
            [contentId]: !prev[contentId]
        }));
    };

    const isVideo = currentAttachment && (currentAttachment.type === 'video' || currentAttachment.type === 'youtube' || currentAttachment.check_type === 2);
    const videoUrl = currentAttachment?.download_url || currentAttachment?.url || "";
    const currentUrl = currentAttachment?.download_url || currentAttachment?.url || "";
    const currentName = (currentAttachment?.name || currentAttachment?.file_name || "").toLowerCase();

    const isPdf = currentAttachment && (currentAttachment.type === 'pdf' || currentName.endsWith('.pdf') || currentUrl.toLowerCase().endsWith('.pdf'));
    const isDoc = currentAttachment && (currentAttachment.type === 'doc' || currentAttachment.type === 'docx' || currentName.endsWith('.doc') || currentName.endsWith('.docx') || currentUrl.toLowerCase().endsWith('.doc') || currentUrl.toLowerCase().endsWith('.docx'));

    return (
        <div className={`flex flex-col h-screen ${t.bg} overflow-hidden`}>
            {/* Header */}
            <header className={`h-16 shrink-0 border-b flex items-center px-4 justify-between ${theme === 'dark' ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate(`/learn/course/${courseId}`)}
                        className={`p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors`}
                    >
                        <ArrowLeft size={20} className={t.text} />
                    </button>
                    <div className="flex flex-col">
                        <h1 className={`text-sm md:text-base font-semibold line-clamp-1 ${t.text}`}>
                            {currentAttachment?.lessonName}
                        </h1>
                        <span className={`text-xs ${t.subtext} line-clamp-1`}>
                            {currentAttachment ? currentAttachment.name : "Loading..."}
                        </span>
                    </div>
                </div>

                <button
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 lg:hidden"
                >
                    <Menu size={20} className={t.text} />
                </button>
            </header>

            <div className="flex flex-1 overflow-hidden relative">
                {/* Main Content Area */}
                <main className={`flex-1 overflow-y-auto bg-black flex flex-col items-center justify-center relative`}>

                    {loading ? (
                        <div className="text-white">Loading content...</div>
                    ) : currentAttachment ? (
                        isVideo ? (
                            <div className="w-full h-full max-w-5xl max-h-screen aspect-video bg-black">
                                <ReactPlayer
                                    src={videoUrl}
                                    config={{
                                        youtube: {
                                            color: 'white',
                                        },
                                    }}
                                    width="100%"
                                    height="100%"
                                    controls
                                    playing
                                />
                            </div>
                        ) : isPdf ? (
                            <div className="w-full h-full max-w-5xl bg-white">
                                <iframe
                                    src={currentAttachment.download_url || currentAttachment.url}
                                    className="w-full h-full border-0"
                                    title={currentAttachment.name}
                                />
                            </div>
                        ) : (
                            <div className="text-center p-8">
                                {/* <div className="mb-4 text-white/80">Content type: {currentAttachment.type}</div> */}
                                <a
                                    href={currentAttachment.download_url || currentAttachment.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    download={isDoc ? (currentAttachment.name || 'download') : undefined}
                                    className="px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition"
                                >
                                    {isDoc ? 'ดาวน์โหลดไฟล์' : 'เปิดลิงค์'}
                                </a>
                            </div>
                        )
                    ) : (
                        <div className="text-white/50">Select content to view</div>
                    )}

                </main>

                {/* Sidebar */}
                <aside
                    className={`
                        fixed inset-y-0 right-0 z-20 w-80 transform transition-transform duration-300 ease-in-out
                        border-l flex flex-col
                        ${sidebarOpen ? 'translate-x-0' : 'translate-x-full'}
                        lg:relative lg:translate-x-0 
                        ${theme === 'dark' ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}
                    `}
                >
                    <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center">
                        <h3 className={`font-semibold ${t.text}`}>Course Content</h3>
                        <button onClick={() => setSidebarOpen(false)} className="lg:hidden">
                            <X size={20} className={t.subtext} />
                        </button>
                    </div>

                    <div className="overflow-y-auto flex-1 p-4 space-y-4">
                        {contents.map((content, idx) => {
                            const cid = content.content_id || content.id;
                            const isOpen = openContents[cid];
                            const lessons = contentLessons[cid] || [];

                            return (
                                <div key={cid} className={`rounded-xl border ${theme === 'dark' ? 'border-gray-800' : 'border-gray-100'} overflow-hidden`}>
                                    <button
                                        onClick={() => toggleContent(cid)}
                                        className={`w-full flex items-center justify-between p-3 text-left ${theme === 'dark' ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}`}
                                    >
                                        <span className={`font-medium text-sm ${t.text}`}>{content.content_name || content.name}</span>
                                        {isOpen ? <ChevronDown size={16} className={t.subtext} /> : <ChevronRight size={16} className={t.subtext} />}
                                    </button>

                                    {isOpen && (
                                        <div className={`border-t ${theme === 'dark' ? 'border-gray-800 bg-gray-800/20' : 'border-gray-100 bg-gray-50/50'}`}>
                                            {lessons.map(lesson => (
                                                <div key={lesson.lesson_id} className="p-2">
                                                    <div className={`text-xs font-semibold px-2 mb-1 ${t.subtext}`}>{lesson.lesson_name || lesson.name}</div>
                                                    <div className="space-y-1">
                                                        {(lesson.attachments || []).map(att => {
                                                            const isActive = att.id.toString() === attachmentId;
                                                            const isVid = att.type === 'video' || att.type === 'youtube' || att.check_type === 2;

                                                            return (
                                                                <button
                                                                    key={att.id}
                                                                    onClick={() => handleAttachmentClick(att)}
                                                                    className={`
                                                                       w-full flex items-center gap-3 p-2 rounded-lg text-left transition-colors text-sm
                                                                       ${isActive
                                                                            ? 'bg-primary/10 text-primary font-medium'
                                                                            : `hover:bg-gray-200/50 dark:hover:bg-gray-700/50 ${t.subtext}`
                                                                        }
                                                                   `}
                                                                >
                                                                    {isVid ? <MonitorPlay size={14} /> : <FileText size={14} />}
                                                                    <span className="line-clamp-1">{att.name || att.file_name}</span>
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </aside>
            </div>
        </div>
    );
};

export default LearnerContentPage;
